import React, { useCallback, useEffect, useState } from "react";
import { Save, ExternalLink } from "lucide-react";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../hooks/useToast";
import Button from "../common/Button";

/**
 * DirectLinkConfigPanel — tradie-side editor for the per-tradie customisation
 * fields stored on `businesses.direct_link_config` (JSONB).
 *
 * Honoured shape:
 *   {
 *     intro_headline:    string,
 *     intro_subhead:     string,
 *     thank_you_message: string,
 *     photos_enabled:    boolean,   // default true
 *   }
 *
 * Self-contained — owns its own load/save lifecycle so it doesn't entangle
 * with TradieSettings' main form state.
 *
 * @param {Object} props
 * @param {string} props.businessId  - profile.business_id of the logged-in tradie
 */
export default function DirectLinkConfigPanel({ businessId }) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    intro_headline: "",
    intro_subhead: "",
    thank_you_message: "",
    photos_enabled: true,
  });

  const load = useCallback(async () => {
    if (!businessId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("businesses")
      .select("direct_link_config")
      .eq("id", businessId)
      .single();
    if (error) {
      showToast(`Couldn't load direct-link config: ${error.message}`, "error");
    } else if (data?.direct_link_config) {
      const c = data.direct_link_config || {};
      setConfig({
        intro_headline: c.intro_headline || "",
        intro_subhead: c.intro_subhead || "",
        thank_you_message: c.thank_you_message || "",
        photos_enabled: c.photos_enabled !== false,
      });
    }
    setLoading(false);
  }, [businessId, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  function set(field, value) {
    setConfig((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    if (!businessId) {
      showToast("Business profile not loaded yet.", "error");
      return;
    }
    setSaving(true);
    const payload = {
      intro_headline: config.intro_headline.trim() || null,
      intro_subhead: config.intro_subhead.trim() || null,
      thank_you_message: config.thank_you_message.trim() || null,
      photos_enabled: config.photos_enabled !== false,
    };
    const { error } = await supabase
      .from("businesses")
      .update({ direct_link_config: payload })
      .eq("id", businessId);
    setSaving(false);
    if (error) {
      showToast(`Save failed: ${error.message}`, "error");
      return;
    }
    showToast("Direct-link customisation saved.", "success");
  }

  if (loading) {
    return <p className="muted small">Loading…</p>;
  }

  // Build a preview of the link for the tradie.
  const directLinkUrl =
    typeof window !== "undefined" && businessId
      ? `${window.location.origin}/?tradie=${businessId}`
      : "";

  return (
    <div>
      {directLinkUrl && (
        <div
          style={{
            padding: "10px 12px",
            background: "var(--surface-2, #FAFAFA)",
            border: "1px solid var(--hairline, rgba(140,100,80,0.18))",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
            flexWrap: "wrap",
            marginBottom: "16px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0, flex: "1 1 220px" }}>
            <span style={{ fontSize: "0.7rem", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-soft)", fontWeight: 700 }}>
              Your direct link
            </span>
            <code
              style={{
                fontSize: "0.82rem",
                color: "var(--text)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {directLinkUrl}
            </code>
          </div>
          <a
            href={directLinkUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 12px",
              background: "transparent",
              border: "1px solid var(--hairline, rgba(140,100,80,0.18))",
              borderRadius: "8px",
              fontSize: "0.82rem",
              fontWeight: 600,
              color: "var(--text)",
              textDecoration: "none",
              flexShrink: 0,
            }}
          >
            <ExternalLink size={13} /> Preview
          </a>
        </div>
      )}

      <div className="form-grid" style={{ gap: "12px" }}>
        <div className="full">
          <label>Welcome headline</label>
          <p className="muted small" style={{ margin: "2px 0 6px" }}>
            Replaces the default "Request a quote from {`{your name}`}". Leave blank to use the default.
          </p>
          <input
            type="text"
            placeholder="e.g. Get a free roofing quote — usually back same day"
            value={config.intro_headline}
            onChange={(e) => set("intro_headline", e.target.value)}
            maxLength={100}
          />
        </div>

        <div className="full">
          <label>Welcome subhead</label>
          <p className="muted small" style={{ margin: "2px 0 6px" }}>
            One short sentence under the headline. Sets expectations for the customer.
          </p>
          <input
            type="text"
            placeholder="e.g. Servicing all of Brisbane and the Bayside. ABN-registered, fully insured."
            value={config.intro_subhead}
            onChange={(e) => set("intro_subhead", e.target.value)}
            maxLength={180}
          />
        </div>

        <div className="full">
          <label>Thank-you message (after submit)</label>
          <p className="muted small" style={{ margin: "2px 0 6px" }}>
            Shown on the success screen once the customer submits their request.
          </p>
          <textarea
            rows={3}
            placeholder="e.g. Thanks! I'll review your request and get back to you within the hour."
            value={config.thank_you_message}
            onChange={(e) => set("thank_you_message", e.target.value)}
            maxLength={400}
          />
        </div>

        <div className="full">
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              cursor: "pointer",
              userSelect: "none",
              padding: "12px 14px",
              background: "var(--surface-2, #FAFAFA)",
              border: "1px solid var(--hairline, rgba(140,100,80,0.18))",
              borderRadius: "10px",
            }}
          >
            <input
              type="checkbox"
              checked={config.photos_enabled !== false}
              onChange={(e) => set("photos_enabled", e.target.checked)}
              style={{ width: "auto", margin: 0 }}
            />
            <span style={{ flex: 1 }}>
              <span style={{ fontWeight: 600, display: "block" }}>Allow photo uploads</span>
              <span className="muted small">
                Customers can attach photos of the job. Turn off if photos aren't useful for your trade.
              </span>
            </span>
          </label>
        </div>
      </div>

      <div className="button-row" style={{ marginTop: "16px" }}>
        <Button onClick={handleSave} disabled={saving}>
          <Save size={15} />
          {saving ? "Saving…" : "Save customisation"}
        </Button>
      </div>
    </div>
  );
}
